/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Heading, { Level } from "@tiptap/extension-heading"; // For headers (h1, h2, h3)
import Bold from "@tiptap/extension-bold"; // For bold text
import Underline from "@tiptap/extension-underline"; // For underlined text
import BulletList from "@tiptap/extension-bullet-list"; // For bullet lists
import OrderedList from "@tiptap/extension-ordered-list"; // For ordered lists
import ListItem from "@tiptap/extension-list-item"; // For list items
import axios from "axios";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import ErrorMessage from "@/components/ErrorMessage";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isEmptyHTML } from "@/utils/helpers";
import { useTranslation } from "react-i18next";

// Toolbar Component

const Toolbar = ({ editor }: { editor: Editor | null }) => {
  const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsHeaderDropdownOpen(false); // Close the dropdown
      }
    };

    // Add event listener when the dropdown is open
    if (isHeaderDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isHeaderDropdownOpen]); // Re-run effect when dropdown state changes

  if (!editor) return null;

  // Handle header change
  const handleHeaderChange = (level: Level | 0) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run(); // Reset to normal text
    } else {
      editor.chain().focus().toggleHeading({ level }).run(); // Set header level
    }

    setIsHeaderDropdownOpen(false);
  };

  // Get the current active header level
  const activeHeaderLevel = editor.getAttributes("heading").level || 0;

  // Handle click outside to close the dropdown


  return (
    <div className="flex flex-wrap gap-2 rounded-t-lg border-b bg-gray-50 p-2">
      {/* Header Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsHeaderDropdownOpen(!isHeaderDropdownOpen)}
          className={`rounded-md p-2 ${activeHeaderLevel
            ? "bg-sidebar text-skyblue"
            : "text-gray-600 hover:bg-gray-100"
            }`}
        >
          {activeHeaderLevel ? `H${activeHeaderLevel}` : "Paragraph"}
        </button>

        {/* Dropdown Menu */}
        {isHeaderDropdownOpen && (
          <div className="absolute z-10 mt-2 w-40 rounded-md border border-gray-200 bg-white shadow-lg">
            <button
              onClick={() => handleHeaderChange(0)}
              className={`w-full px-4 py-2 text-left ${activeHeaderLevel === 0
                ? "bg-sidebar text-skyblue"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              Paragraph
            </button>
            <button
              onClick={() => handleHeaderChange(1)}
              className={`w-full px-4 py-2 text-left ${activeHeaderLevel === 1
                ? "bg-sidebar text-skyblue"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              H1
            </button>
            <button
              onClick={() => { handleHeaderChange(2) }}
              className={`w-full px-4 py-2 text-left ${activeHeaderLevel === 2
                ? "bg-sidebar text-skyblue"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              H2
            </button>
            <button
              onClick={() => handleHeaderChange(3)}
              className={`w-full px-4 py-2 text-left ${activeHeaderLevel === 3
                ? "bg-sidebar text-skyblue"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              H3
            </button>
            {/* Uncomment if you need H4, H5, H6
            <button
              onClick={() => handleHeaderChange(4)}
              className={`w-full px-4 py-2 text-left ${
                activeHeaderLevel === 4
                  ? "bg-sidebar text-skyblue"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              H4
            </button>
            <button
              onClick={() => handleHeaderChange(5)}
              className={`w-full px-4 py-2 text-left ${
                activeHeaderLevel === 5
                  ? "bg-sidebar text-skyblue"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              H5
            </button>
            <button
              onClick={() => handleHeaderChange(6)}
              className={`w-full px-4 py-2 text-left ${
                activeHeaderLevel === 6
                  ? "bg-sidebar text-skyblue"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              H6
            </button>
            */}
          </div>
        )}
      </div>

      {/* Bold */}
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`rounded-md p-2 ${editor.isActive("bold")
          ? "bg-sidebar text-skyblue"
          : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        <strong>B</strong>
      </button>

      {/* Underline */}
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`rounded-md p-2 ${editor.isActive("underline")
          ? "bg-sidebar text-skyblue"
          : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        <u>U</u>
      </button>

      {/* Bullet List */}
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`rounded-md p-2 ${editor.isActive("bulletList")
          ? "bg-sidebar text-skyblue"
          : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        • List
      </button>

      {/* Ordered List */}
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`rounded-md p-2 ${editor.isActive("orderedList")
          ? "bg-sidebar text-skyblue"
          : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        1. List
      </button>
    </div>
  );
};


const TermsAndPrivacyPolicySettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [activeTab, setActiveTab] = useState<"terms" | "privacy">("terms");
  const [language, setLanguage] = useState<"en" | "ar">("ar");

  const [termsContent, setTermsContent] = useState<{ en: string; ar: string }>({
    en: "",
    ar: "",
  });
  const [privacyContent, setPrivacyContent] = useState<{
    en: string;
    ar: string;
  }>({
    en: "",
    ar: "",
  });
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNeedRefetch, setIsNeedRefetch] = useState(false);

  const langOptions = [
    { value: "en", label: t("termsPrivacy.english") },
    { value: "ar", label: t("termsPrivacy.arabic") },
  ];

  // TipTap Editor for Terms
  const termsEditor = useEditor({
    extensions: [
      StarterKit,
      Heading.configure({
        // levels: [1, 2, 3,4,5,6],
        levels: [1, 2, 3],
      }),
      Bold,
      Underline,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: termsContent[language] || "",
    onUpdate: ({ editor }) => {
      setTermsContent((prev) => ({ ...prev, [language]: editor.getHTML() }));
    },
  });

  // TipTap Editor for Privacy
  const privacyEditor = useEditor({
    extensions: [
      StarterKit,
      Heading.configure({
        // levels: [1, 2, 3,4,5,6],
        levels: [1, 2, 3],
      }),
      Bold,
      Underline,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: privacyContent[language] || "",
    onUpdate: ({ editor }) => {
      setPrivacyContent((prev) => ({ ...prev, [language]: editor.getHTML() }));
    },
  });

  useEffect(
    () => {
      if (termsEditor) {
        termsEditor.commands.setContent(termsContent[language] || "");
      }
      if (privacyEditor) {
        privacyEditor.commands.setContent(privacyContent[language] || "");
      }
    },

    [
      language,
      termsContent.ar !== "",
      termsContent.en !== "",
      privacyContent.ar !== "",
      privacyContent.en !== "",
      termsEditor,
      privacyEditor,
    ],
  );

  useEffect(() => {
    const textEditor = document.querySelector(".ProseMirror");
    textEditor?.classList.add(language === "ar" ? "rtl" : "ltr");
    textEditor?.classList.remove(language === "ar" ? "ltr" : "rtl");
  }, [language]);

  // Fetch data on component mount
  useEffect(() => {
    if (!token) return

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      setIsNeedRefetch(false);

      try {
        const [termsResponse, privacyResponse] = await Promise.all([
          // Fetch terms content
          axios.get(
            `${BASE_URL}/api/dashboard/settings/static-pages/terms-and-conditions?locale=${lang}`,
            {
              headers: {
                Authorization: token,
              },
            },
          ),
          // Fetch privacy content
          axios.get(
            `${BASE_URL}/api/dashboard/settings/static-pages/privacy-policy?locale=${lang}`,
            {
              headers: {
                Authorization: token,
              },
            },
          ),
        ]);

        // Handle terms content response
        if (termsResponse.data.success) {
          setTermsContent({
            en: termsResponse.data.contentEN,
            ar: termsResponse.data.contentAR,
          });
        } else {
          const message =
            termsResponse.data.error ||
            t("termsPrivacy.fetchTermsFailed")
          setError(message);
          toast.error(message);
        }

        // Handle privacy content response
        if (privacyResponse.data.success) {
          setPrivacyContent({
            en: privacyResponse.data.contentEN,
            ar: privacyResponse.data.contentAR,
          });
        } else {
          const message =
            privacyResponse.data.error ||
            t("termsPrivacy.fetchPrivacyFailed")
          setError(message);
          toast.error(message);
        }
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            t("termsPrivacy.fetchBothFailed")
          setError(message);
          toast.error(message);
        } else {
          console.error(error);
          const message = t("termsPrivacy.unexpectedError") as string;
          setError(message);
          toast.error(message);
        }
      } finally {
        setIsLoading(false); // Set loading state to false
      }
    };

    fetchData();
  }, [BASE_URL, lang, token, t, isNeedRefetch]);

  useEffect(() => {
    setLanguage("ar");
  }, [lang]);

  // Save data for the active tab
  const saveData = async () => {
    setIsLoading(true); // Set loading state to true

    try {
      const url =
        activeTab === "terms"
          ? `${BASE_URL}/api/dashboard/settings/static-pages/terms-and-conditions?locale=${lang}`
          : `${BASE_URL}/api/dashboard/settings/static-pages/privacy-policy?locale=${lang}`;

      const content = activeTab === "terms" ? termsContent : privacyContent;

      const response = await axios.put(
        url,
        {
          contentEN: isEmptyHTML(content.en) ? "" : content.en,
          contentAR: isEmptyHTML(content.ar) ? "" : content.ar,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response.data.success) {
        toast.success(
          t(
            activeTab === "terms"
              ? "termsPrivacy.saveSuccessTerms"
              : "termsPrivacy.saveSuccessPrivacy"
          ));
        setIsNeedRefetch(true);
        setLanguage("ar");
      } else {
        const message =
          response.data.error ||
          t(
            activeTab === "terms"
              ? "termsPrivacy.saveFailedTerms"
              : "termsPrivacy.saveFailedPrivacy"
          );
        setError(message);
        toast.error(message);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          t(
            activeTab === "terms"
              ? "termsPrivacy.saveFailedTerms"
              : "termsPrivacy.saveFailedPrivacy"
          );
        setError(message);
        toast.error(message);
      } else {
        console.error(error);
        const message = t("termsPrivacy.unexpectedError") as string;
        setError(message);
        toast.error(message);
      }
    } finally {
      setIsLoading(false); // Set loading state to false
    }
  };

  const handleChangeTab = (tab: "terms" | "privacy") => {
    if (activeTab === tab) return;
    setLanguage("ar");
    setActiveTab(tab);
  };

  if (isLoading)
    return (
      <>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-[50px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[40px] w-[150px] dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[300px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
          <div className="flex flex-col gap-4">
            <Skeleton className="h-[40px] w-[200px] self-end dark:bg-slate-600 dark:bg-opacity-35" />
          </div>
        </div>
      </>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="mx-auto w-full">
      {/* Tabs */}
      <div className="mb-6 flex space-x-4 border-b">
        <button
          onClick={() => handleChangeTab("terms")}
          className={`px-4 py-2 ${activeTab === "terms"
            ? "border-b-2 border-sidebar font-bold"
            : "text-gray-500"
            }`}
        >
          {t("termsPrivacy.terms")}
        </button>
        <button
          onClick={() => handleChangeTab("privacy")}
          className={`px-4 py-2 ${activeTab === "privacy"
            ? "border-b-2 border-sidebar font-bold"
            : "text-gray-500"
            }`}
        >
          {t("termsPrivacy.privacy")}
        </button>
      </div>

      {/* Language Selector */}
      <div className="mb-6 flex w-full items-center justify-between">
        <h3 className="m-0 text-lg font-semibold">
        {t(activeTab === "terms" ? "termsPrivacy.terms" : "termsPrivacy.privacy")}

        </h3>

        <Select
          value={language}
          onValueChange={(value) => setLanguage(value as "en" | "ar")}
        >
          <SelectTrigger className="w-[110px] bg-sidebar text-skyblue dark:bg-sidebar dark:text-skyblue">
            <SelectValue className="" />
          </SelectTrigger>
          <SelectContent className="border-none bg-sidebar dark:bg-sidebar">
            {langOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="bg-sidebar text-skyblue focus:bg-skyblue focus:text-sidebar active:bg-skyblue active:text-sidebar dark:bg-sidebar dark:text-skyblue focus:dark:bg-skyblue focus:dark:text-sidebar"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col ">
        {/* Toolbar */}
        {activeTab === "terms" && <Toolbar editor={termsEditor} />}
        {activeTab === "privacy" && <Toolbar editor={privacyEditor} />}

        {/* TipTap Editor */}
        <div className="rtl">
          {activeTab === "terms" && (
            <EditorContent editor={termsEditor} className="tiptap-editor" />
          )}
          {activeTab === "privacy" && (
            <EditorContent editor={privacyEditor} className="tiptap-editor" />
          )}
        </div>

        {/* Save Button */}
        <button
          onClick={saveData}
          className="mt-5 w-fit self-end rounded-md bg-sidebar px-4 py-2 text-skyblue"
        >
          {t(
            activeTab === "terms"
              ? "termsPrivacy.saveTerms"
              : "termsPrivacy.savePrivacy"
          )}
        </button>
      </div>
    </div>
  );
};

export default TermsAndPrivacyPolicySettings;
